import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    OnDestroy,
    OnInit,
    SecurityContext,
    ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { marked } from 'marked';
import { Subject, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs';
import {
    HelpArticle,
    HelpArticleSummary,
    HelpArticlesMeta,
    HelpChatService,
    HelpSourceArticle,
} from './help-chat.service';

/** Mensagem exibida na conversa (local). */
export interface HelpChatMessage
{
    role: 'user' | 'assistant';
    content: string;
    /**
     * HTML SANITIZADO (DomSanitizer) da resposta do assistente — renderizado
     * apenas nas bolhas do ASSISTENTE. O usuário continua texto puro.
     */
    renderedHtml?: string | null;
    sourceArticle: HelpSourceArticle | null;
    suggestedFollowups: HelpSourceArticle[];
}

const DEFAULT_SUGGESTIONS: string[] = [
    'Como lançar uma entrada?',
    'Como cadastrar um membro?',
    'Como gerar relatórios?',
    'Como funciona o faturamento?',
];

/**
 * Frases de status rotativas exibidas enquanto o assistente processa a pergunta
 * (latência real ~5-15s — 2 chamadas LLM). Rotação SEQUENCIAL CÍCLICA
 * (determinística — facilita validação automatizada) a cada STATUS_ROTATION_MS.
 * Reinicia a cada novo envio; some quando a resposta chega (ou no erro, quando
 * o banner assume). NUNCA vira bolha de mensagem na lista.
 */
const STATUS_PHRASES: readonly string[] = [
    'Pensando...',
    'Compreendendo sua pergunta...',
    'Consultando a base de conhecimento...',
    'Analisando os artigos...',
    'Processando resposta...',
    'Quase pronto...',
];

/** Intervalo de rotação das frases de status (ms). */
const STATUS_ROTATION_MS = 2000;

@Component({
    selector       : 'app-help-chat',
    templateUrl    : './help-chat.component.html',
    standalone     : true,
    imports        : [
        CommonModule,
        FormsModule,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule,
        MatProgressSpinnerModule,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HelpChatComponent implements OnInit, OnDestroy
{
    @ViewChild('messagesContainer')
    private _messagesContainer: ElementRef<HTMLDivElement>;

    // ---------------------------------------------------------------------
    // @ Tabs
    // ---------------------------------------------------------------------
    activeTab: 'chat' | 'articles' = 'chat';

    // ---------------------------------------------------------------------
    // @ Chat — estado
    // ---------------------------------------------------------------------
    messages: HelpChatMessage[] = [];
    inputMessage: string = '';
    sending: boolean = false;
    /** Frase de status rotativa exibida enquanto `sending` — linha discreta, sem bolha. */
    activeStatusPhrase: string = '';
    conversationId: string | null = null;
    chatError: string | null = null;
    readonly suggestions: string[] = DEFAULT_SUGGESTIONS;

    // ---------------------------------------------------------------------
    // @ Artigos — estado
    // ---------------------------------------------------------------------
    articlesView: 'list' | 'detail' = 'list';
    articles: HelpArticleSummary[] = [];
    articlesMeta: HelpArticlesMeta | null = null;
    categories: string[] = [];
    selectedCategory: string = '';
    searchTerm: string = '';
    articlesLoading: boolean = false;
    articlesLoadingMore: boolean = false;
    articlesError: string | null = null;
    selectedArticle: HelpArticle | null = null;
    articleParagraphs: string[] = [];

    private readonly _searchDebounce$: Subject<string> = new Subject<string>();
    private readonly _unsubscribeAll: Subject<any> = new Subject<any>();

    /** Timer de rotação das frases de status — limpo no ngOnDestroy e a cada parada. */
    private _statusTimer: ReturnType<typeof setInterval> | null = null;
    private _statusPhraseIndex: number = 0;

    /**
     * Constructor
     */
    constructor(
        public dialogRef: MatDialogRef<HelpChatComponent>,
        private _helpService: HelpChatService,
        private _changeDetectorRef: ChangeDetectorRef,
        private _sanitizer: DomSanitizer
    )
    {
    }

    // ---------------------------------------------------------------------
    // @ Lifecycle hooks
    // ---------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void
    {
        // Busca com debounce (300ms) — dispara apenas com term >= 3 chars
        // (ou busca vazia para voltar à listagem completa).
        this._searchDebounce$
            .pipe(
                debounceTime(300),
                distinctUntilChanged(),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe((term: string) => {
                this.searchTerm = term;
                this._reloadArticleList();
            });

        // Pré-carrega a listagem de artigos (pagina 1 + categorias), adiado para o
        // próximo macrotask: disparar HTTP no MESMO tick da abertura do dialog faz o
        // FuseLoadingInterceptor acender o fuse-loading-bar (show false→true) depois
        // da primeira checagem de CD do app → NG0100 em dev build. Adiar = o request
        // roda em tick próprio (mudança detectada na passada 1 do próprio tick), sem NG0100.
        setTimeout(() => this._loadArticlesPage(1, true), 0);
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void
    {
        this._stopStatusRotation();
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // ---------------------------------------------------------------------
    // @ Modal / tabs
    // ---------------------------------------------------------------------

    /**
     * Fecha o modal
     */
    closeModal(): void
    {
        this.dialogRef.close();
    }

    /**
     * Troca a aba ativa ('chat' | 'articles')
     */
    selectTab(tab: 'chat' | 'articles'): void
    {
        this.activeTab = tab;
        this._changeDetectorRef.markForCheck();
    }

    // ---------------------------------------------------------------------
    // @ Chat
    // ---------------------------------------------------------------------

    /**
     * Envia uma sugestão/chip como mensagem
     */
    sendSuggestion(text: string): void
    {
        this.inputMessage = text;
        this.sendMessage();
    }

    /**
     * Envia a mensagem para o assistente (multi-turno via conversationId).
     * Resposta pode demorar até ~25s (LLM real) — o estado `sending` mantém a
     * linha de STATUS ROTATIVO (frases discretas, sem bolha) e bloqueia reenvio.
     */
    sendMessage(): void
    {
        if (!this.inputMessage.trim() || this.sending)
        {
            return;
        }

        const text = this.inputMessage.trim();

        this.messages.push({
            role: 'user',
            content: text,
            sourceArticle: null,
            suggestedFollowups: [],
        });
        this.inputMessage = '';
        this.chatError = null;
        this.sending = true;
        this._startStatusRotation();
        this._changeDetectorRef.markForCheck();
        this._scrollToBottom();

        this._helpService.ask(text, this.conversationId)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe({
                next: (response) => {
                    this._stopStatusRotation();
                    this.conversationId = response.data.conversationId ?? null;
                    const answer = response.data.answer;
                    this.messages.push({
                        role: 'assistant',
                        content: answer,
                        // Markdown → HTML SANITIZADO (nunca innerHTML cru).
                        renderedHtml: this._renderMarkdown(answer),
                        sourceArticle: response.data.sourceArticle,
                        suggestedFollowups: response.data.suggestedFollowups ?? [],
                    });
                    this.sending = false;
                    this._changeDetectorRef.markForCheck();
                    this._scrollToBottom();
                },
                error: (error) => {
                    this._stopStatusRotation();
                    this.sending = false;
                    this.chatError = this._extractErrorMessage(
                        error,
                        'Não foi possível obter resposta do assistente agora. Tente novamente em instantes.'
                    );
                    this._changeDetectorRef.markForCheck();
                    this._scrollToBottom();
                }
            });
    }

    /**
     * Nova conversa: limpa o histórico local e o conversationId.
     */
    newConversation(): void
    {
        this._stopStatusRotation();
        this.messages = [];
        this.chatError = null;
        this.conversationId = null;
        this.sending = false;
        this.activeStatusPhrase = '';
        this.inputMessage = '';
        this._changeDetectorRef.markForCheck();
    }

    /**
     * Chip de fonte citada na resposta → vai para a aba Artigos e abre o artigo.
     */
    openSourceArticle(article: HelpSourceArticle): void
    {
        this.activeTab = 'articles';
        this._openArticle(article.slug);
    }

    /**
     * Followup sugerido pelo assistente → envia como nova pergunta.
     */
    sendFollowup(article: HelpSourceArticle): void
    {
        this.sendSuggestion(article.title);
    }

    // ---------------------------------------------------------------------
    // @ Artigos — listagem
    // ---------------------------------------------------------------------

    /**
     * Dispara a busca com debounce (300ms); reload da página 1.
     */
    onSearchChange(value: string): void
    {
        this._searchDebounce$.next(value);
    }

    /**
     * Seletor de categoria ('' = Todas).
     */
    selectCategory(category: string): void
    {
        this.selectedCategory = category;
        this._reloadArticleList();
    }

    /**
     * Paginação simples: "Carregar mais" enquanto houver próxima página.
     */
    loadMoreArticles(): void
    {
        if (this.articlesLoading || this.articlesLoadingMore || !this.articlesMeta)
        {
            return;
        }

        if (this.articlesMeta.current_page >= this.articlesMeta.last_page)
        {
            return;
        }

        this._loadArticlesPage(this.articlesMeta.current_page + 1, false);
    }

    private _reloadArticleList(): void
    {
        this._loadArticlesPage(1, true);
    }

    private _loadArticlesPage(page: number, replace: boolean): void
    {
        if (replace)
        {
            if (this.articlesLoading)
            {
                return;
            }
            this.articlesLoading = true;
        }
        else
        {
            if (this.articlesLoadingMore)
            {
                return;
            }
            this.articlesLoadingMore = true;
        }

        this.articlesError = null;
        this._changeDetectorRef.markForCheck();

        const params: { term?: string; category?: string; page: number; perPage: number } = {
            page,
            perPage: 20,
        };

        const term = this.searchTerm?.trim() ?? '';
        if (term.length >= 3)
        {
            params.term = term;
        }

        if (this.selectedCategory)
        {
            params.category = this.selectedCategory;
        }

        this._helpService.listArticles(params)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe({
                next: (response) => {
                    this.categories = response.data.categories;
                    this.articlesMeta = response.data.meta;
                    this.articles = replace
                        ? response.data.items
                        : this._mergeArticles(this.articles, response.data.items);
                    this.articlesLoading = false;
                    this.articlesLoadingMore = false;
                    this._changeDetectorRef.markForCheck();
                },
                error: (error) => {
                    this.articlesLoading = false;
                    this.articlesLoadingMore = false;
                    this.articlesError = this._extractErrorMessage(
                        error,
                        'Não foi possível carregar os artigos. Tente novamente.'
                    );
                    this._changeDetectorRef.markForCheck();
                }
            });
    }

    private _mergeArticles(
        current: HelpArticleSummary[],
        incoming: HelpArticleSummary[]
    ): HelpArticleSummary[]
    {
        const ids = new Set<number>(current.map(a => a.id));

        return [...current, ...incoming.filter(a => !ids.has(a.id))];
    }

    // ---------------------------------------------------------------------
    // @ Artigos — leitura
    // ---------------------------------------------------------------------

    /**
     * Abre um artigo pelo slug (da lista).
     */
    openArticle(slug: string): void
    {
        this._openArticle(slug);
    }

    private _openArticle(slug: string): void
    {
        this.articlesView = 'detail';
        this.selectedArticle = null;
        this.articleParagraphs = [];
        this.articlesError = null;
        this._changeDetectorRef.markForCheck();

        this._helpService.getArticle(slug)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe({
                next: (response) => {
                    this.selectedArticle = response.data;
                    // Body renderizado em parágrafos — NUNCA innerHTML.
                    this.articleParagraphs = response.data.body
                        .split('\n')
                        .map(p => p.trim())
                        .filter(p => p.length > 0);
                    this._changeDetectorRef.markForCheck();
                },
                error: (error) => {
                    this.articlesError = this._extractErrorMessage(
                        error,
                        'Artigo não encontrado.'
                    );
                    this._changeDetectorRef.markForCheck();
                }
            });
    }

    /**
     * Volta da leitura para a listagem.
     */
    backToList(): void
    {
        this.articlesView = 'list';
        this.selectedArticle = null;
        this.articleParagraphs = [];
        this.articlesError = null;
        this._changeDetectorRef.markForCheck();
    }

    // ---------------------------------------------------------------------
    // @ Helpers
    // ---------------------------------------------------------------------

    trackBySlug(_index: number, article: HelpArticleSummary): string
    {
        return article.slug;
    }

    /**
     * Renderiza a resposta do assistente em markdown → HTML.
     *
     * SELEÇÃO: `breaks: true` preserva quebras de linha simples (mesmo do
     * `whitespace-pre-line` anterior); `gfm: true` habilita listas/títulos.
     * O HTML retornado passa pelo SANITIZADOR do Angular (SecurityContext.HTML),
     * que remove scripts/event handlers/URLs perigosas — nunca renderizamos HTML
     * cru. Enquanto o backend não redeployar, markdown literal (`**`, `###`)
     * aparece como texto comum — o render é seguro para AMBOS os estados.
     */
    private _renderMarkdown(content: string): string | null
    {
        if (!content)
        {
            return '';
        }

        const rawHtml = marked.parse(content, { async: false, breaks: true, gfm: true }) as string;

        return this._sanitizer.sanitize(SecurityContext.HTML, rawHtml);
    }

    private _scrollToBottom(): void
    {
        setTimeout(() => {
            const container = this._messagesContainer?.nativeElement;

            if (container)
            {
                container.scrollTop = container.scrollHeight;
            }
        }, 50);
    }

    /**
     * Inicia a rotação SEQUENCIAL CÍCLICA das frases de status (reinicia na
     * primeira frase a cada envio). OnPush: mudança disparada por timer exige
     * `markForCheck()` explícito.
     */
    private _startStatusRotation(): void
    {
        this._stopStatusRotation();
        this._statusPhraseIndex = 0;
        this.activeStatusPhrase = STATUS_PHRASES[0];
        this._statusTimer = setInterval(() =>
        {
            this._statusPhraseIndex = (this._statusPhraseIndex + 1) % STATUS_PHRASES.length;
            this.activeStatusPhrase = STATUS_PHRASES[this._statusPhraseIndex];
            this._changeDetectorRef.markForCheck();
        }, STATUS_ROTATION_MS);
    }

    /** Para o timer de rotação (idempotente — sem leak). */
    private _stopStatusRotation(): void
    {
        if (this._statusTimer !== null)
        {
            clearInterval(this._statusTimer);
            this._statusTimer = null;
        }
    }

    private _extractErrorMessage(error: any, fallback: string): string
    {
        const body = error?.error;

        if (body && typeof body === 'object' && typeof body.message === 'string')
        {
            return body.message;
        }

        return typeof error?.message === 'string' ? error.message : fallback;
    }
}